import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponse, PaginatedApiResponse, USE_ALL_SITES } from '@card-software/nexus-utilities';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ItemMaster, ItemMasterCodeBatchCommand, ItemMasterFilter } from '../../models/item-master';

@Injectable({
  providedIn: 'root',
})
export class ItemMasterProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.itemApi + '/api/v1/item';

  public getAllItems(): Observable<ApiResponse<ItemMaster[]>> {
    return this.httpClient.get<ApiResponse<ItemMaster[]>>(`${this.baseUrl}/all`);
  }

  postItemSearch(
    dataSearch: ItemMasterFilter,
    includeInactive = false,
    matchAny = false,
    excludeProductDevelopments = false,
  ): Observable<ApiResponse<PaginatedApiResponse<ItemMaster>>> {
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ItemMaster>>>(
      `${this.baseUrl}/search?includeInactive=${includeInactive}&matchAny=${matchAny}&excludeProductDevelopments=${excludeProductDevelopments}`,
      dataSearch,
    );
  }

  search(
    dataSearch: ItemMasterFilter,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
    includeInactive = false,
    useAllSites = false,
  ): Observable<ApiResponse<PaginatedApiResponse<ItemMaster>>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}&includeInactive=${includeInactive}`;
    if (sortField) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder ?? -1}`;
    }
    const context = new HttpContext().set(USE_ALL_SITES, useAllSites);
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ItemMaster>>>(url, dataSearch, { context });
  }

  getItemCodesBatch(command: ItemMasterCodeBatchCommand): Observable<ApiResponse<ItemMaster[]>> {
    return this.httpClient.post<ApiResponse<ItemMaster[]>>(`${this.baseUrl}/item-codes/batch`, command);
  }

  postItemMaster(
    itemMaster: ItemMaster,
    itemProposalReference?: string,
    itemRequestReference?: string,
  ): Observable<ApiResponse<ItemMaster>> {
    let params = new HttpParams();

    if (itemProposalReference) {
      params = params.set('itemProposalReference', itemProposalReference);
    }

    // ADD THIS:
    if (itemRequestReference) {
      params = params.set('itemRequestReference', itemRequestReference);
    }

    return this.httpClient.post<ApiResponse<ItemMaster>>(`${this.baseUrl}`, itemMaster, { params });
  }

  public deleteItemMasterById(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  public uploadFiles(paramsForSent: { itemCode?: string; isMain: number }, files: File[]): Observable<ApiResponse<void>> {
    let params = new HttpParams();

    if (paramsForSent.itemCode) {
      params = params.set('itemCode', paramsForSent.itemCode);
    }
    params = params.set('isMain', paramsForSent.isMain.toString());

    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    return this.httpClient.post<ApiResponse<void>>(`${this.baseUrl}/upload-files`, formData, {
      params,
    });
  }
}
